import type { Scene } from '../components/Scenes';
import {
  isSceneLinkedToAnyChapter,
  isSceneLinkedToContext,
  sceneDocumentIdOf,
  type SceneLinkEntityRef,
} from './scene-links';

export interface AvailableScenesChapterSection {
  id: string;
  title: string;
  order: number;
  scenes: Scene[];
}

export interface AvailableScenesDocumentSection {
  documentId: string;
  documentTitle: string;
  unlinkedScenes: Scene[];
  chapters: AvailableScenesChapterSection[];
}

export interface AvailableScenesTree {
  currentUnlinkedScenes: Scene[];
  documents: AvailableScenesDocumentSection[];
}

export interface AvailableScenesChapterRef {
  id: string;
  title: string;
  order: number;
  documentId: string;
  documentTitle: string;
}

function sceneSort(a: Scene, b: Scene): number {
  return (a.order ?? 0) - (b.order ?? 0);
}

function chapterSort(a: AvailableScenesChapterSection, b: AvailableScenesChapterSection): number {
  return a.order - b.order || a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
}

function documentSort(a: AvailableScenesDocumentSection, b: AvailableScenesDocumentSection): number {
  return a.documentTitle.localeCompare(b.documentTitle, undefined, { sensitivity: 'base' });
}

export function flattenAvailableScenes(tree: AvailableScenesTree): Scene[] {
  const scenes: Scene[] = [...tree.currentUnlinkedScenes];
  for (const document of tree.documents) {
    scenes.push(...document.unlinkedScenes);
    for (const chapter of document.chapters) {
      scenes.push(...chapter.scenes);
    }
  }
  return scenes;
}

export function buildAvailableScenesTree(params: {
  scenes: Scene[];
  chapters: AvailableScenesChapterRef[];
  refs: SceneLinkEntityRef[];
  currentDocumentId: string;
}): AvailableScenesTree {
  const { scenes, chapters, refs, currentDocumentId } = params;

  const availableScenes = scenes.filter((scene) => {
    const sceneDocumentId = sceneDocumentIdOf(scene, currentDocumentId);
    return !isSceneLinkedToAnyChapter(refs, scene.id, sceneDocumentId, currentDocumentId);
  });

  const currentUnlinkedScenes = availableScenes
    .filter((scene) => sceneDocumentIdOf(scene, currentDocumentId) === currentDocumentId)
    .sort(sceneSort);

  const chaptersByDocument = new Map<string, AvailableScenesChapterRef[]>();
  for (const chapter of chapters) {
    if (chapter.documentId === currentDocumentId) continue;
    const list = chaptersByDocument.get(chapter.documentId) ?? [];
    list.push(chapter);
    chaptersByDocument.set(chapter.documentId, list);
  }

  const scenesByDocument = new Map<string, Scene[]>();
  for (const scene of availableScenes) {
    const sceneDocumentId = sceneDocumentIdOf(scene, currentDocumentId);
    if (sceneDocumentId === currentDocumentId) continue;
    const list = scenesByDocument.get(sceneDocumentId) ?? [];
    list.push(scene);
    scenesByDocument.set(sceneDocumentId, list);
  }

  const documents: AvailableScenesDocumentSection[] = [];

  for (const [documentId, documentScenes] of scenesByDocument) {
    const documentChapters = [...(chaptersByDocument.get(documentId) ?? [])].sort(
      (a, b) => a.order - b.order || a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
    );
    const documentTitle =
      documentScenes.find((scene) => scene.documentTitle)?.documentTitle ??
      documentChapters[0]?.documentTitle ??
      documentId;

    const linkedSceneIds = new Set<string>();
    const chapterSections: AvailableScenesChapterSection[] = [];

    for (const chapter of documentChapters) {
      const chapterScenes = documentScenes
        .filter((scene) =>
          isSceneLinkedToContext(
            refs,
            scene.id,
            documentId,
            { type: 'chapter', id: chapter.id },
            documentId,
          ),
        )
        .sort(sceneSort);

      for (const scene of chapterScenes) {
        linkedSceneIds.add(scene.id);
      }

      if (chapterScenes.length > 0) {
        chapterSections.push({
          id: chapter.id,
          title: chapter.title,
          order: chapter.order,
          scenes: chapterScenes,
        });
      }
    }

    const unlinkedScenes = documentScenes
      .filter((scene) => !linkedSceneIds.has(scene.id))
      .sort(sceneSort);

    if (unlinkedScenes.length === 0 && chapterSections.length === 0) {
      continue;
    }

    documents.push({
      documentId,
      documentTitle,
      unlinkedScenes,
      chapters: chapterSections.sort(chapterSort),
    });
  }

  return {
    currentUnlinkedScenes,
    documents: documents.sort(documentSort),
  };
}
